import React, { useState, useEffect } from 'react';
import jsPDF from 'jspdf';
import styles from './CsvPreviewModal.module.css';
import {
    getAccountingExportDraft,
    resetAccountingExportDraft,
    saveAccountingExportDraft
} from '../services/accountingExportDraftApi';
import { getOptionalInvoiceReference } from '../services/accountingDocuments';
import { mergeDraftRows, selectPreviewRows } from './csvPreviewDraftUtils';

/**
 * CSV 미리보기 모달 공용 컴포넌트
 * 
 * @param {boolean} isOpen - 모달 열림 상태
 * @param {function} onClose - 모달 닫기 핸들러
 * @param {array} data - 미리보기 데이터 배열
 * @param {function} onDownloadCsv - CSV 다운로드 핸들러 (selectedData, selectedRows)
 * @param {function} onDownloadFacture - Facture PDF 다운로드 핸들러 (selectedData, selectedRows) - optional
 * @param {boolean} showFactureButton - Facture 버튼 표시 여부
 * @param {string} lang - 언어 설정 ('ko', 'fr', 'en')
 * @param {string} refPrefix - 레퍼런스 접두사 ('T' for Tattoo, 'A' for Acompte)
 * @param {string} draftType - Server draft type ('tattoo' or 'deposit')
 * @param {number} draftYear - Draft period year
 * @param {number} draftMonth - Draft period month
 */
const CsvPreviewModal = ({
    isOpen,
    onClose,
    data = [],
    onDownloadCsv,
    onDownloadFacture,
    showFactureButton = false,
    lang = 'fr',
    refPrefix = 'T',
    draftType,
    draftYear,
    draftMonth
}) => {
    const [previewData, setPreviewData] = useState([]);
    const [selectedRows, setSelectedRows] = useState(new Set());
    const [bulkMoyenFrom, setBulkMoyenFrom] = useState('');
    const [bulkMoyenTo, setBulkMoyenTo] = useState('');
    const [draftLoading, setDraftLoading] = useState(false);
    const [draftSaving, setDraftSaving] = useState(false);
    const [draftDirty, setDraftDirty] = useState(false);
    const [draftUpdatedAt, setDraftUpdatedAt] = useState(null);
    const [draftError, setDraftError] = useState('');

    const hasDraftScope = Boolean(draftType && draftYear && draftMonth);

    // 데이터 변경 시 초기화
    useEffect(() => {
        if (!isOpen || data.length === 0) return undefined;

        let cancelled = false;
        const loadDraft = async () => {
            setDraftLoading(hasDraftScope);
            setDraftError('');
            setDraftDirty(false);
            setPreviewData(data);
            setSelectedRows(new Set(data.map(row => row.id)));

            if (!hasDraftScope) {
                setPreviewData(data);
                setSelectedRows(new Set(data.map(row => row.id)));
                setDraftUpdatedAt(null);
                setDraftLoading(false);
                return;
            }

            try {
                const result = await getAccountingExportDraft(draftType, draftYear, draftMonth);
                if (cancelled) return;
                const { mergedData, selected } = mergeDraftRows(data, result.rows || [], result.exists);
                setPreviewData(mergedData);
                setSelectedRows(selected);
                setDraftUpdatedAt(result.updated_at || null);
            } catch (error) {
                if (cancelled) return;
                console.error('Accounting draft load error:', error);
                setPreviewData(data);
                setSelectedRows(new Set(data.map(row => row.id)));
                setDraftUpdatedAt(null);
                setDraftError(lang === 'ko' ? '저장된 초안을 불러오지 못했습니다.' : lang === 'fr' ? 'Impossible de charger le brouillon.' : 'Could not load the saved draft.');
            } finally {
                if (!cancelled) setDraftLoading(false);
            }
        };

        loadDraft();
        return () => { cancelled = true; };
    }, [isOpen, data, draftType, draftYear, draftMonth, hasDraftScope, lang]);

    useEffect(() => {
        if (!isOpen) {
            setSelectedRows(new Set());
            setBulkMoyenFrom('');
            setBulkMoyenTo('');
            setDraftLoading(false);
            setDraftSaving(false);
            setDraftDirty(false);
            setDraftUpdatedAt(null);
            setDraftError('');
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSelectAll = (checked) => {
        if (checked) {
            setSelectedRows(new Set(previewData.map(r => r.id)));
        } else {
            setSelectedRows(new Set());
        }
        setDraftDirty(true);
    };

    const handleRowSelect = (id, checked) => {
        const newSet = new Set(selectedRows);
        if (checked) {
            newSet.add(id);
        } else {
            newSet.delete(id);
        }
        setSelectedRows(newSet);
        setDraftDirty(true);
    };

    const handleTtcChange = (id, newTtc) => {
        const ttc = parseFloat(newTtc) || 0;
        const ht = ttc / 1.20;
        const tva = ttc - ht;
        setPreviewData(prev => prev.map(r =>
            r.id === id
                ? { ...r, ttc: ttc.toFixed(2), tva: tva.toFixed(2), ht: ht.toFixed(2) }
                : r
        ));
        setDraftDirty(true);
    };

    const handleMoyenChange = (id, newMoyen) => {
        setPreviewData(prev => prev.map(r =>
            r.id === id ? { ...r, moyen: newMoyen } : r
        ));
        setDraftDirty(true);
    };

    const moyenOptions = ['PayPal', 'Lydia', 'Espèces', 'Virement', 'Carte', 'Autre'];

    const handleBulkMoyenApply = () => {
        if (!bulkMoyenFrom || !bulkMoyenTo || bulkMoyenFrom === bulkMoyenTo) return;
        setPreviewData(prev => prev.map(r =>
            r.moyen === bulkMoyenFrom ? { ...r, moyen: bulkMoyenTo } : r
        ));
        setBulkMoyenFrom('');
        setBulkMoyenTo('');
        setDraftDirty(true);
    };

    const buildDraftRows = () => previewData.map(row => ({
        reservation_id: row.id,
        ttc: row.ttc,
        moyen: row.moyen || '-',
        client: row.client || '',
        payment_date: row.payment_date,
        selected: selectedRows.has(row.id)
    }));

    const handleSaveDraft = async () => {
        if (!hasDraftScope || draftSaving) return;
        setDraftSaving(true);
        setDraftError('');
        try {
            const result = await saveAccountingExportDraft(draftType, draftYear, draftMonth, buildDraftRows());
            setDraftUpdatedAt(result.updated_at || new Date().toISOString());
            setDraftDirty(false);
        } catch (error) {
            console.error('Accounting draft save error:', error);
            setDraftError(lang === 'ko' ? '초안을 저장하지 못했습니다.' : lang === 'fr' ? 'Impossible d\'enregistrer le brouillon.' : 'Could not save the draft.');
        } finally {
            setDraftSaving(false);
        }
    };

    const handleResetDraft = async () => {
        if (!hasDraftScope || draftSaving) return;
        const message = lang === 'ko'
            ? '저장된 초안을 삭제하고 원본값으로 되돌릴까요?'
            : lang === 'fr'
                ? 'Supprimer le brouillon et restaurer les valeurs d’origine ?'
                : 'Delete the draft and restore the original values?';
        if (!window.confirm(message)) return;

        setDraftSaving(true);
        setDraftError('');
        try {
            await resetAccountingExportDraft(draftType, draftYear, draftMonth);
            setPreviewData(data);
            setSelectedRows(new Set(data.map(row => row.id)));
            setDraftUpdatedAt(null);
            setDraftDirty(false);
        } catch (error) {
            console.error('Accounting draft reset error:', error);
            setDraftError(lang === 'ko' ? '초안을 초기화하지 못했습니다.' : lang === 'fr' ? 'Impossible de réinitialiser le brouillon.' : 'Could not reset the draft.');
        } finally {
            setDraftSaving(false);
        }
    };

    const handleClose = () => {
        if (draftDirty) {
            const message = lang === 'ko'
                ? '저장하지 않은 변경사항이 있습니다. 닫을까요?'
                : lang === 'fr'
                    ? 'Des modifications ne sont pas enregistrées. Fermer ?'
                    : 'There are unsaved changes. Close anyway?';
            if (!window.confirm(message)) return;
        }
        onClose();
    };

    const handleDownloadCsv = () => {
        const selectedData = selectPreviewRows(previewData, selectedRows);
        onDownloadCsv?.(selectedData, selectedRows, previewData);
    };

    const handleDownloadFacture = () => {
        const selectedData = selectPreviewRows(previewData, selectedRows);
        onDownloadFacture?.(selectedData, selectedRows, previewData);
    };

    // PDF 표 다운로드
    const handleDownloadPdf = () => {
        const selectedData = selectPreviewRows(previewData, selectedRows);
        if (selectedData.length === 0) return;

        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 14;
        let y = 20;

        // 타이틀
        const typeLabel = { T: 'Tattoo', D: 'Acompte', A: 'Acompte', C: 'Commission' }[refPrefix] || '';
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(`R\u00e9capitulatif${typeLabel ? ` - ${typeLabel}` : ''}`, pageWidth / 2, y, { align: 'center' });
        y += 12;

        // 테이블 설정
        const cols = [
            { label: 'Date', width: 22, align: 'left' },
            { label: 'TTC', width: 22, align: 'right' },
            { label: 'TVA', width: 22, align: 'right' },
            { label: 'HT', width: 22, align: 'right' },
            { label: 'Moyen', width: 22, align: 'left' },
            { label: 'Client', width: 40, align: 'left' },
            { label: 'R\u00e9f', width: 32, align: 'left' }
        ];
        const rowHeight = 6;
        const fontSize = 8;

        // 헤더
        doc.setFillColor(240, 240, 240);
        doc.rect(margin, y, pageWidth - margin * 2, rowHeight + 2, 'F');
        doc.setFontSize(fontSize);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(80);
        let x = margin + 2;
        cols.forEach(col => {
            const tx = col.align === 'right' ? x + col.width - 2 : x;
            doc.text(col.label, tx, y + rowHeight - 1, { align: col.align === 'right' ? 'right' : 'left' });
            x += col.width;
        });
        y += rowHeight + 2;

        // 구분선
        doc.setDrawColor(200);
        doc.setLineWidth(0.3);
        doc.line(margin, y, pageWidth - margin, y);
        y += 1;

        // 데이터 행
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(30);

        // 한글 → ASCII 헬퍼
        const safeText = (text) => {
            if (!text) return '-';
            // jsPDF 기본 폰트는 라틴만 지원 - 한글 제거
            return text.replace(/[\u3131-\u3163\uac00-\ud7a3]/g, '') || text.substring(0, 1) + '...';
        };

        selectedData.forEach((row, index) => {
            // 페이지 넘김 체크
            if (y > doc.internal.pageSize.getHeight() - 30) {
                doc.addPage();
                y = 20;
            }

            // 짝수행 배경
            if (index % 2 === 0) {
                doc.setFillColor(250, 250, 250);
                doc.rect(margin, y - 1, pageWidth - margin * 2, rowHeight, 'F');
            }

            const ref = getOptionalInvoiceReference(row);
            const values = [
                row.date || '-',
                `${row.ttc}\u20ac`,
                `${row.tva}\u20ac`,
                `${row.ht}\u20ac`,
                row.moyen || '-',
                safeText(row.client),
                ref
            ];

            x = margin + 2;
            doc.setFontSize(fontSize);
            cols.forEach((col, i) => {
                const tx = col.align === 'right' ? x + col.width - 2 : x;
                let text = values[i];
                // 텍스트 길이 제한
                const maxChars = Math.floor(col.width / 1.8);
                if (text.length > maxChars) text = text.substring(0, maxChars - 1) + '..';
                doc.text(text, tx, y + rowHeight - 2, { align: col.align === 'right' ? 'right' : 'left' });
                x += col.width;
            });
            y += rowHeight;
        });

        // 하단 구분선
        y += 2;
        doc.setDrawColor(180);
        doc.setLineWidth(0.5);
        doc.line(margin, y, pageWidth - margin, y);
        y += 6;

        // TTC 총합
        const totalTTC = selectedData.reduce((sum, r) => sum + (parseFloat(r.ttc) || 0), 0);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(30);
        doc.text('Total TTC:', margin + 2, y);
        doc.text(`${totalTTC.toFixed(2)}\u20ac`, pageWidth - margin - 2, y, { align: 'right' });
        y += 7;

        // Moyen별 소계
        const ttcByMoyen = selectedData.reduce((acc, r) => {
            const key = r.moyen || '-';
            acc[key] = (acc[key] || 0) + (parseFloat(r.ttc) || 0);
            return acc;
        }, {});

        if (Object.keys(ttcByMoyen).length > 0) {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            doc.setTextColor(100);
            const moyenParts = Object.entries(ttcByMoyen)
                .sort((a, b) => b[1] - a[1])
                .map(([moyen, amount]) => `${moyen}: ${amount.toFixed(2)}\u20ac`);
            doc.text(moyenParts.join('  |  '), margin + 2, y);
        }

        // 파일명
        const firstRow = selectedData[0];
        const filename = `recap_${refPrefix}_${firstRow?.refYear || ''}_${firstRow?.refMonth || ''}.pdf`;
        doc.save(filename);
    };

    // 선택된 항목 중 인덱스 계산
    const getReference = (row) => {
        const isSelected = selectedRows.has(row.id);
        if (!isSelected) return '-';
        return getOptionalInvoiceReference(row);
    };

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10000
            }}
            onClick={handleClose}
        >
            <div
                className="csv-preview-modal"
                style={{
                    backgroundColor: '#fff',
                    borderRadius: '12px',
                    padding: '20px',
                    maxWidth: '90vw',
                    maxHeight: '80vh',
                    overflow: 'auto',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* 전역 스타일 오버라이드 */}
                <style>
                    {`
            .csv-preview-modal input[type="number"],
            .csv-preview-modal select {
              background-color: transparent !important;
              background: transparent !important;
            }
          `}
                </style>

                {/* 헤더 */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                        {lang === 'ko' ? 'CSV 미리보기' : lang === 'fr' ? 'Aperçu CSV' : 'CSV Preview'}
                    </h3>
                    <button
                        onClick={handleClose}
                        style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#666' }}
                    >
                        ×
                    </button>
                </div>

                {hasDraftScope && (
                    <div className={styles.draftStatusBar}>
                        <span className={draftError
                            ? styles.draftStatusError
                            : draftDirty
                                ? styles.draftStatusDirty
                                : styles.draftStatus}
                        >
                            {draftLoading
                                ? (lang === 'ko' ? '초안 불러오는 중...' : lang === 'fr' ? 'Chargement du brouillon...' : 'Loading draft...')
                                : draftError || (draftDirty
                                    ? (lang === 'ko' ? '저장하지 않은 변경사항' : lang === 'fr' ? 'Modifications non enregistrées' : 'Unsaved changes')
                                    : draftUpdatedAt
                                        ? `${lang === 'ko' ? '저장됨' : lang === 'fr' ? 'Brouillon enregistré' : 'Draft saved'} · ${new Date(draftUpdatedAt).toLocaleString(lang === 'ko' ? 'ko-KR' : lang === 'fr' ? 'fr-FR' : 'en-US')}`
                                        : (lang === 'ko' ? '저장된 초안 없음' : lang === 'fr' ? 'Aucun brouillon enregistré' : 'No saved draft'))}
                        </span>
                        <div className={styles.draftActions}>
                            <button
                                type="button"
                                onClick={handleResetDraft}
                                disabled={draftLoading || draftSaving || (!draftUpdatedAt && !draftDirty)}
                                className={`${styles.draftButton} ${styles.draftResetButton}`}
                            >
                                {lang === 'ko' ? '초기화' : lang === 'fr' ? 'Réinitialiser' : 'Reset'}
                            </button>
                            <button
                                type="button"
                                onClick={handleSaveDraft}
                                disabled={draftLoading || draftSaving || !draftDirty}
                                className={`${styles.draftButton} ${styles.draftSaveButton}`}
                            >
                                {draftSaving
                                    ? (lang === 'ko' ? '저장 중...' : lang === 'fr' ? 'Enregistrement...' : 'Saving...')
                                    : (lang === 'ko' ? '초안 저장' : lang === 'fr' ? 'Enregistrer le brouillon' : 'Save draft')}
                            </button>
                        </div>
                    </div>
                )}

                {/* 전체 선택 */}
                <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                        type="checkbox"
                        checked={selectedRows.size === previewData.length && previewData.length > 0}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        style={{ cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '12px', textTransform: 'uppercase' }}>
                        {lang === 'ko' ? '전체 선택' : lang === 'fr' ? 'Tout sélectionner' : 'Select All'}
                        ({selectedRows.size}/{previewData.length})
                    </span>
                </div>

                {/* Moyen 일괄 변경 */}
                <div style={{
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '12px'
                }}>
                    <span style={{ color: '#666', textTransform: 'uppercase', fontSize: '10px', fontWeight: '600' }}>Moyen</span>
                    <select
                        value={bulkMoyenFrom}
                        onChange={(e) => setBulkMoyenFrom(e.target.value)}
                        style={{
                            padding: '3px 6px',
                            fontSize: '11px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            outline: 'none'
                        }}
                    >
                        <option value="">--</option>
                        {moyenOptions.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                    <span style={{ color: '#999' }}>→</span>
                    <select
                        value={bulkMoyenTo}
                        onChange={(e) => setBulkMoyenTo(e.target.value)}
                        style={{
                            padding: '3px 6px',
                            fontSize: '11px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            outline: 'none'
                        }}
                    >
                        <option value="">--</option>
                        {moyenOptions.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                    <button
                        onClick={handleBulkMoyenApply}
                        disabled={!bulkMoyenFrom || !bulkMoyenTo || bulkMoyenFrom === bulkMoyenTo}
                        style={{
                            padding: '3px 10px',
                            fontSize: '10px',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            backgroundColor: (bulkMoyenFrom && bulkMoyenTo && bulkMoyenFrom !== bulkMoyenTo) ? '#333' : '#ccc',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: (bulkMoyenFrom && bulkMoyenTo && bulkMoyenFrom !== bulkMoyenTo) ? 'pointer' : 'not-allowed'
                        }}
                    >
                        {lang === 'ko' ? '적용' : lang === 'fr' ? 'Appliquer' : 'Apply'}
                    </button>
                </div>

                {/* 테이블 */}
                <div style={{
                    overflowX: 'auto',
                    opacity: draftLoading ? 0.55 : 1,
                    pointerEvents: draftLoading ? 'none' : 'auto'
                }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f5f5f5' }}>
                                <th style={{ padding: '8px 4px', textAlign: 'center', width: '30px' }}></th>
                                <th style={{ padding: '8px', textAlign: 'left' }}>Date</th>
                                <th style={{ padding: '8px', textAlign: 'left' }}>Type</th>
                                <th style={{ padding: '8px', textAlign: 'right' }}>TTC</th>
                                <th style={{ padding: '8px', textAlign: 'right' }}>TVA</th>
                                <th style={{ padding: '8px', textAlign: 'right' }}>HT</th>
                                <th style={{ padding: '8px', textAlign: 'left' }}>Moyen</th>
                                <th style={{ padding: '8px', textAlign: 'left' }}>Client</th>
                                <th style={{ padding: '8px', textAlign: 'left' }}>Réf</th>
                            </tr>
                        </thead>
                        <tbody>
                            {previewData.map((row) => {
                                const isSelected = selectedRows.has(row.id);
                                const reference = getReference(row);

                                return (
                                    <tr
                                        key={row.id}
                                        style={{
                                            backgroundColor: isSelected ? '#e8f5e9' : '#fff',
                                            opacity: isSelected ? 1 : 0.5,
                                            borderBottom: '1px solid #eee'
                                        }}
                                    >
                                        <td style={{ padding: '8px 4px', textAlign: 'center' }}>
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={(e) => handleRowSelect(row.id, e.target.checked)}
                                                style={{ cursor: 'pointer' }}
                                            />
                                        </td>
                                        <td style={{ padding: '8px' }}>{row.date}</td>
                                        <td style={{ padding: '8px' }}>{row.type}</td>
                                        <td style={{ padding: '4px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                                                <input
                                                    type="number"
                                                    value={row.ttc}
                                                    onChange={(e) => handleTtcChange(row.id, e.target.value)}
                                                    style={{
                                                        width: `${Math.max(4, String(row.ttc).length) + 1}ch`,
                                                        padding: '2px 4px',
                                                        fontSize: '12px',
                                                        textAlign: 'right',
                                                        border: '1px dashed #999',
                                                        borderRadius: '2px',
                                                        backgroundColor: 'transparent',
                                                        outline: 'none'
                                                    }}
                                                />
                                                <span>€</span>
                                            </span>
                                        </td>
                                        <td style={{ padding: '8px', textAlign: 'right' }}>{row.tva}€</td>
                                        <td style={{ padding: '8px', textAlign: 'right' }}>{row.ht}€</td>
                                        <td style={{ padding: '4px 8px' }}>
                                            <select
                                                value={row.moyen}
                                                onChange={(e) => handleMoyenChange(row.id, e.target.value)}
                                                style={{
                                                    padding: '2px 4px',
                                                    fontSize: '12px',
                                                    border: '1px dashed #999',
                                                    borderRadius: '2px',
                                                    backgroundColor: 'transparent',
                                                    cursor: 'pointer',
                                                    outline: 'none'
                                                }}
                                            >
                                                <option value="PayPal">PayPal</option>
                                                <option value="Lydia">Lydia</option>
                                                <option value="Espèces">Espèces</option>
                                                <option value="Virement">Virement</option>
                                                <option value="Carte">Carte</option>
                                                <option value="Autre">Autre</option>
                                            </select>
                                        </td>
                                        <td style={{ padding: '4px 8px' }}>
                                            <input
                                                type="text"
                                                value={row.client}
                                                onChange={(e) => {
                                                    setPreviewData(prev => prev.map(r =>
                                                        r.id === row.id ? { ...r, client: e.target.value } : r
                                                    ));
                                                    setDraftDirty(true);
                                                }}
                                                style={{
                                                    width: `${Math.max(4, (row.client || '').length) + 1}ch`,
                                                    padding: '2px 4px',
                                                    fontSize: '12px',
                                                    border: '1px dashed #999',
                                                    borderRadius: '2px',
                                                    backgroundColor: 'transparent',
                                                    outline: 'none'
                                                }}
                                            />
                                        </td>
                                        <td style={{ padding: '8px', fontFamily: 'monospace' }}>{reference}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* TTC 총합 섹션 */}
                {(() => {
                    const selectedData = selectPreviewRows(previewData, selectedRows);
                    const totalTTC = selectedData.reduce((sum, r) => sum + (parseFloat(r.ttc) || 0), 0);
                    const ttcByMoyen = selectedData.reduce((acc, r) => {
                        const key = r.moyen || '-';
                        acc[key] = (acc[key] || 0) + (parseFloat(r.ttc) || 0);
                        return acc;
                    }, {});

                    if (selectedData.length === 0) return null;

                    return (
                        <div style={{
                            marginTop: '12px',
                            padding: '12px',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '8px',
                            fontSize: '12px'
                        }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: Object.keys(ttcByMoyen).length > 1 ? '8px' : '0'
                            }}>
                                <span style={{ fontWeight: '600', textTransform: 'uppercase' }}>
                                    Total TTC
                                </span>
                                <span style={{ fontWeight: '600', fontSize: '14px' }}>
                                    {totalTTC.toFixed(2)}€
                                </span>
                            </div>
                            {Object.keys(ttcByMoyen).length > 1 && (
                                <div style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '8px',
                                    paddingTop: '8px',
                                    borderTop: '1px solid #e0e0e0'
                                }}>
                                    {Object.entries(ttcByMoyen)
                                        .sort((a, b) => b[1] - a[1])
                                        .map(([moyen, amount]) => (
                                            <span key={moyen} style={{
                                                padding: '2px 8px',
                                                backgroundColor: '#fff',
                                                borderRadius: '4px',
                                                border: '1px solid #ddd',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {moyen} <strong>{amount.toFixed(2)}€</strong>
                                            </span>
                                        ))
                                    }
                                </div>
                            )}
                        </div>
                    );
                })()}

                {/* 다운로드 버튼 */}
                <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                    <button
                        onClick={handleClose}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#f5f5f5',
                            color: '#333',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            textTransform: 'uppercase'
                        }}
                    >
                        {lang === 'ko' ? '취소' : lang === 'fr' ? 'Annuler' : 'Cancel'}
                    </button>
                    {/* Facture 버튼 */}
                    {showFactureButton && (
                        <button
                            onClick={handleDownloadFacture}
                            disabled={selectedRows.size === 0}
                            style={{
                                padding: '8px 16px',
                                backgroundColor: selectedRows.size > 0 ? '#1976d2' : '#ccc',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '12px',
                                cursor: selectedRows.size > 0 ? 'pointer' : 'not-allowed',
                                textTransform: 'uppercase'
                            }}
                        >
                            Facture ({selectedRows.size})
                        </button>
                    )}
                    <button
                        onClick={handleDownloadCsv}
                        disabled={selectedRows.size === 0}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: selectedRows.size > 0 ? '#2e7d32' : '#ccc',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            cursor: selectedRows.size > 0 ? 'pointer' : 'not-allowed',
                            textTransform: 'uppercase'
                        }}
                    >
                        CSV ({selectedRows.size})
                    </button>
                    <button
                        onClick={handleDownloadPdf}
                        disabled={selectedRows.size === 0}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: selectedRows.size > 0 ? '#e65100' : '#ccc',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            cursor: selectedRows.size > 0 ? 'pointer' : 'not-allowed',
                            textTransform: 'uppercase'
                        }}
                    >
                        PDF ({selectedRows.size})
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CsvPreviewModal;
